<template>
  <div id="page" class="flex flex-col h-full max-h-full pt-0 items-center">
    <div id="message-area-scroll">
      <div id="message-container">
        <div class="fade fade-in"></div>
        <div v-if="messages.length === 0">
          <div class="flex flex-wrap justify-center gap-3 mt-4 px-4">
            <div
              v-for="prompt in examplePrompts"
              :key="prompt"
              class="w-full md:w-fit text-center p-3 bg-gray-50/30 dark:bg-gray-800/20 border border-gray-200/30 dark:border-gray-700/30 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              @click="userInput = prompt"
            >
              {{ prompt }}
            </div>
          </div>
        </div>
        <div id="message-list">
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="message"
          >
            <chat-message
              :message="message.text"
              :type="message.type"
              :timestamp="message.timestamp"
            />
          </div>
          <chat-message v-if="isLoading" :loading="true" type="bot" />
          <chat-message
            v-if="showParams && queryStore.queryCols.data.length > 0"
            message="Run parameters (see graph traversal above):"
            type="bot"
            :params="showParams"
          />
        </div>
        <div class="fade fade-out"></div>
      </div>
    </div>
    <div id="input-wrapper">
      <div id="input-container">
        <Textarea
          v-model="userInput"
          auto-resize
          rows="2"
          class="input-textarea"
          placeholder="Ask bo a question..."
          @keyup.enter="getResponse"
        />
        <i class="pi pi-send send-icon" @click="getResponse"></i>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import axiosRepo from "@/repositories/AxiosRepository";
import { useQueryStore } from "@/stores/queryStore";
import Textarea from "primevue/textarea";
import ChatMessage from "@/components/chatMessage.vue";

const queryStore = useQueryStore();

const isLoading = ref(false);
const showParams = ref(true);
const userInput = ref("");
const messages = ref([]);
const examplePrompts = ref([
  "Predict the trial result for AstraZenec's AZD0901 clinical trial?",
  "What is the POS for a phase 3 trial of STX-478 (Mutant-Selective PI3Kα Inhibitor) in different solid tumours and countries?",
  "what are the most common genes in US and China lung cancer patients - use GeneCount average as the measure.",
  "what are the key differences in gene expressions in lung and colorectal cancer patients in the US?",
  "what are the most common bacteria present in the tumour microenvironemnt for US lung cancer patients?",
  "how many TNBC samples do you have?",
  "list cancer samples total number per indication",
  "list total number of cancer samples per country",
  "Are there any known chemicals that interact with BRCA1?",
]);

onMounted(() => {
  scrollToLatestMessage();
});

function scrollToLatestMessage() {
  const element = document.getElementById("message-area-scroll");
  element.scrollTop = 300;
}

function addMessage(text, type) {
  messages.value.push({
    text,
    type,
    timestamp: new Date().toLocaleTimeString(),
  });
  scrollToLatestMessage();
}

async function getResponse() {
  if (userInput.value.trim() === "") return;

  showParams.value = false;

  // Add user message to messages
  addMessage(userInput.value, "user");

  // Set loading state
  let query = userInput.value;
  userInput.value = "";
  isLoading.value = true;

  try {
    // Await the response from the endpoint
    const response = await axiosRepo.run_chatboql_query(
      query,
      queryStore.chatThreadId
    );
    console.debug("llm response:", response.data);

    const {
      path_through_knowledge_graph,
      groupby_columns,
      aggregate_columns,
      filter_columns,
      message_to_user,
    } = response.data;

    // Add bot response to messages
    isLoading.value = false;
    addMessage(message_to_user || "No bot message provided", "bot");

    if (!path_through_knowledge_graph) return;

    queryStore.resetUI(true);
    // Update joinStack and queryCols in queryStore
    if (path_through_knowledge_graph) {
      path_through_knowledge_graph.forEach((table) => {
        queryStore.addNodeToJoinStackById(table);
      });
    }
    if (groupby_columns) {
      groupby_columns.forEach((column) => {
        queryStore.addQueryColByLabel(column);
      });
    }
    if (aggregate_columns) {
      aggregate_columns.forEach((measure) => {
        const { column, aggregation } = measure;
        queryStore.addQueryColByLabel(column, "measure", {
          measure_type: aggregation,
        });
      });
    }
    if (filter_columns) {
      filter_columns.forEach((filter) => {
        let { column, operator, value } = filter;
        if (!Array.isArray(value)) value = [value];
        queryStore.addQueryColByLabel(column, "filter", {
          filter_operator: operator,
          filter_value: value,
        });
      });
    }
    new Promise((resolve) => setTimeout(resolve, 1500)).then(
      () => (showParams.value = true)
    );
  } catch (error) {
    console.error("Error in generating response from llm:", error);

    addMessage("Sorry I encountered an error. Please try again", "bot");
  } finally {
    isLoading.value = false;
  }

  // Clear input
  userInput.value = "";
}
</script>

<style scoped>
#message-area-scroll {
  display: flex;
  flex-direction: column-reverse;
  flex-grow: 1;
  position: relative;
  width: 100%;
  overflow: auto;
  padding: 0 1rem;
}

#placeholder-examples {
  display: flex;
  flex-direction: column-reverse;
  flex-grow: 1;
  position: relative;
  width: 100%;
  overflow: auto;
  padding: 0 1rem;
}

#message-container {
  margin: 0 auto;
  width: 100%;
  max-width: 55rem;
  position: relative;
}

.fade {
  height: 2rem;
  width: 100%;
  position: sticky;
}

.fade-in {
  @extend .fade;
  @apply bg-gradient-to-b from-[var(--p-surface-0)]
  dark:from-[var(--p-surface-900)] to-transparent;
  top: 0;
}

.fade-out {
  @extend .fade;
  @apply bg-gradient-to-b from-transparent to-[var(--p-surface-0)]
  dark:to-[var(--p-surface-900)];
  bottom: 0;
}

#message-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

#input-wrapper {
  max-width: 45rem;
  width: 100%;
  padding: 0 1rem 1rem 1rem;
}

#input-container {
  @apply shadow bg-surface-100 dark:bg-surface-800;
  padding-right: 0.75rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.input-textarea {
  flex-grow: 1;
  max-height: 8rem;
  overflow: auto !important;
  background-color: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
}

.send-icon {
  font-size: 1rem !important;
  padding: 0.5rem;
  color: inherit;
  cursor: pointer;
}

.send-icon:hover {
  @apply text-primary;
}
</style>
